import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Tab, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, IconButton, Chip, TextField, InputAdornment } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Person as PersonIcon, Business as BusinessIcon, Group as GroupIcon, Email, Phone, CheckCircle, Cancel, Search as SearchIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { getBusinessDetails } from '../../../services/admin.service';
import { Employee, CoOwner } from '../../../validation/business.validation';
import { Customer } from '../../../validation/customer.validation';
import Loader from '../../../common/loader';
import {formatPhoneNumber} from '../../../helper/phoneFormatter.helper';

const BusinessDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [coOwners, setCoOwners] = useState<CoOwner[]>([]);
  const [businessName, setBusinessName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchBusinessDetails = async () => {
      if (!id) return;
      try {
        const data = await getBusinessDetails(id);
        console.log('data', data)
        setBusinessName(data?.businessName ?? '');
        setEmployees(data.employees || []);
        setCustomers(data.customers || []);
        setCoOwners(data.coOwners || []);
      } catch (error) {
        console.error('Error fetching business details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinessDetails();
  }, [id]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getFullName = (row: any) => {
    const firstName = row?.firstName || '';
    const middleName = row?.middleName || '';
    const lastName = row?.lastName || '';
    return `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim();
  };

  const filterData = (data: any[]) => {
    if (!searchTerm) return data;
    const normalized = searchTerm.toLowerCase();
    return data.filter((item) => {
      const fullName = getFullName(item).toLowerCase();
      return (
        fullName.includes(normalized) ||
        item.email?.toLowerCase().includes(normalized) ||
        item.phone?.toLowerCase().includes(normalized)
      );
    });
  };


  const renderTable = (data: any[], columns: string[], type: string) => (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
      <Table>
        <TableHead sx={{ bgcolor: 'primary.main' }}>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col} sx={{ color: 'white', fontWeight: 'bold' }}>
                {col.charAt(0).toUpperCase() + col.slice(1)}
              </TableCell>
            ))}
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
            {type === 'Customers' && <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + (type === 'Customers' ? 3 : 2)} align="center" sx={{ py: 6 }}>
                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <GroupIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">No {type.toLowerCase()} found</Typography>
                  <Typography variant="body2">This business doesn't have any {type.toLowerCase()} yet.</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, index) => (
              <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                {columns.map((col) => (
                  <TableCell key={col}>
                    {col === 'email' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Email sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                        <Typography variant="body2">{row[col] || 'N/A'}</Typography>
                      </Box>
                    ) : col === 'phone' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Phone sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                        <Typography variant="body2">{formatPhoneNumber(row[col]) || 'N/A'}</Typography>
                      </Box>
                    ) : col === 'name' ? (
                      <Typography variant="body2">{getFullName(row) || 'N/A'}</Typography>
                    ) : (
                      <Typography variant="body2">{row[col] || 'N/A'}</Typography>
                    )}

                  </TableCell>
                ))}
                <TableCell>
                  <Chip
                    icon={row.isActive ? <CheckCircle /> : <Cancel />}
                    label={row.isActive ? 'Active' : 'Inactive'}
                    color={row.isActive ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                {type === 'Customers' && (
                  <TableCell>
                    <IconButton
                      onClick={() => navigate(`/admin/customer/${row.id}`, { state: { businessId: id } })}
                      color="primary"
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div className="min-h-screen">
      <div className="mx-auto">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-4 sm:mb-6">
            <IconButton
              onClick={() => navigate('/admin/business')}
              color="primary"
              size="small"
              sx={{
                bgcolor: 'white',
                boxShadow: 2,
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                '&:hover': { bgcolor: 'grey.50', transform: 'scale(1.05)' },
                transition: 'all 0.2s',
              }}
            >
              <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
            <div>
              <Typography variant="h5" className="font-semibold text-gray-700 mt-1">
                {businessName || '—'}
              </Typography>
            </div>
          </div>
        </div>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="business details tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: { xs: 48, sm: 64 },
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 500,
                  minWidth: { xs: 120, sm: 'auto' },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                },
                '& .MuiTabs-scrollButtons': {
                  display: { xs: 'flex', sm: 'none' },
                },
              }}
            >
              <Tab
                icon={<BusinessIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />}
                iconPosition="start"
                label={`Customers (${customers.length})`}
                sx={{ minHeight: { xs: 48, sm: 64 } }}
              />
              <Tab
                icon={<PersonIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />}
                iconPosition="start"
                label={`Employees (${employees.length})`}
                sx={{ minHeight: { xs: 48, sm: 64 } }}
              />
              <Tab
                icon={<GroupIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />}
                iconPosition="start"
                label={`Co-owners (${coOwners.length})`}
                sx={{ minHeight: { xs: 48, sm: 64 } }}
              />
            </Tabs>
          </Box>

          <Box>
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader />
              </div>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={`Search ${activeTab === 0 ? 'Customers' : activeTab === 1 ? 'Employees' : 'Co-owners'} by name, email, or phone`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {activeTab === 1 && renderTable(filterData(employees), ['name', 'email', 'phone'], 'Employees')}
                {activeTab === 2 && renderTable(filterData(coOwners), ['name', 'email', 'phone'], 'Co-owners')}
                {activeTab === 0 && renderTable(filterData(customers), ['name', 'email', 'phone'], 'Customers')}
              </>
            )}
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default BusinessDetails;
